import React, { useEffect, useMemo } from 'react';
import { format, startOfDay, endOfDay, eachDayOfInterval } from 'date-fns';
import { useTheme, Box } from '@mui/material';


const getDayColor = (date, palette) => {
    const day = new Date(date).getDay();
    if (day === 6) return palette.info.light;
    if (day === 0) return palette.error.light;
    return 'inherit';
};

const Timeline = React.forwardRef(({ cellWidth = 20, rowHeight = 60, from, to }, ref) => {
    const theme = useTheme();

    const hourStep = useMemo(() => {
        if (cellWidth < 4) return 12;
        if (cellWidth < 8) return 4;
        if (cellWidth <= 12) return 2;
        return 1;
    }, [cellWidth]);

    const dates = useMemo(() => {
        if (!from || !to) return [];
        try {
            return eachDayOfInterval({
                start: startOfDay(from),
                end: endOfDay(to),
            }).map(date => ({ date, label: format(date, 'dd MMM') }));
        } catch {
            return [{ date: new Date(), label: format(new Date(), 'dd MMM') }];
        }
    }, [from, to]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const onWheel = (e) => {
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                e.preventDefault();
                el.scrollLeft += e.deltaY;
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [ref]);

    const boxWidth = cellWidth * hourStep;

    return (
        <Box
            ref={ref}
            sx={{
                width: '100%',
                height: rowHeight,
                display: 'flex',
                overflowX: 'hidden',
                overflowY: 'scroll',
                bgcolor: 'background.default',
                borderBottom: `1px solid ${theme.palette.neutral.deep}`,
                '&::-webkit-scrollbar-track': { backgroundColor: 'background.default' },
            }}
        >
            {dates.map((d, index) => (
                <Box key={format(d.date, 'yyyy-MM-dd')} sx={{ flexShrink: 0 }}>
                    {/* Day label */}
                    <Box sx={{
                        minWidth: 24 * cellWidth,
                        height: rowHeight / 2 - 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.8rem',
                        color: getDayColor(d.date, theme.palette),
                        borderLeft: index === 0 ? `1px solid ${theme.palette.divider}` : undefined,
                        borderRight: `1px solid ${theme.palette.divider}`,
                    }}>
                        {d.label}
                    </Box>

                    {/* Hour slots */}
                    <Box sx={{
                        display: 'flex',
                        height: rowHeight / 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        borderLeft: index === 0 ? `1px solid ${theme.palette.divider}` : undefined,
                    }}>
                        {Array.from({ length: 24 / hourStep }, (_, i) => i * hourStep).map(h => (
                            <Box key={h} sx={{
                                width: boxWidth,
                                minWidth: boxWidth,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.65rem',
                                color: 'text.secondary',
                                borderRight: `1px solid ${theme.palette.divider}`,
                                boxSizing: 'border-box',
                            }}>
                                {boxWidth > 12 ? h : ''}
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
});

export default React.memo(Timeline);
